import { useEffect, useState } from 'react';
import { Badge, Button } from '@/design-system';
import { type Build, fetchBuild } from '@/lib/build';
import { SLICES } from './slices';
import styles from './BuildPage.module.css';

/**
 * How far the build has got, for staff (decision 13). It was the public landing
 * page's in App Starter; a service applicants arrive at should open on what they
 * came for, not on its own construction.
 *
 * The list is data and the running build draws the line (CLAUDE.md, Conventions).
 */
export function BuildPage() {
  const [build, setBuild] = useState<Build | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchBuild()
      .then((loaded) => {
        if (!cancelled) setBuild(loaded);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Not found (or not yet known) marks nothing as done, rather than guessing.
  const reached = build ? SLICES.findIndex((slice) => slice.id === build.slice) : -1;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>The build</h1>
      <p className={styles.lede}>
        Each slice of this application in order, marked against the version running here.
      </p>
      {build?.apiDocs && (
        <div>
          <Button href="/api/docs" variant="secondary">
            API documentation
          </Button>
        </div>
      )}
      <section aria-labelledby="progress" className={styles.progress}>
        <h2 id="progress" className={styles.progressTitle}>
          How it was built
        </h2>
        <ol className={styles.slices}>
          {SLICES.map((slice, index) => {
            const done = reached >= 0 && index <= reached;
            const next = reached >= 0 && index === reached + 1;
            return (
              <li key={slice.id} className={[styles.slice, done ? '' : styles.pending].join(' ')}>
                <span className={styles.sliceId}>{slice.id}</span>
                <span className={styles.sliceText}>
                  <span className={styles.sliceName}>{slice.name}</span>
                  <span className={styles.sliceWhat}>{slice.what}</span>
                </span>
                <span className={styles.sliceState}>
                  {slice.released && done && <Badge tone="accent">{slice.released}</Badge>}
                  {done && <Badge tone="success">Done</Badge>}
                  {next && <Badge tone="neutral">Next</Badge>}
                </span>
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}
